import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon as FAIcon } from "@fortawesome/react-fontawesome";
import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import { forwardRef } from "react";

import { Button } from "../../components/button/Button";
import { cn } from "../../utils/cn";

type titleWeight = "light" | "normal" | "medium" | "semibold" | "bold" | "extrabold";

const renderTitleStyle = (titleWeight: titleWeight = "semibold") => `text-base font-${titleWeight}`;

interface CollapsibleProps
  extends React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Root> {
  header: string;
  titleWeight?: titleWeight;
}

const Collapsible = forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.Root>,
  CollapsibleProps
>(({ className, children, header, titleWeight, open, ...props }, ref) => (
  <CollapsiblePrimitive.Root
    ref={ref}
    className={cn(
      "w-full rounded-sm border border-solid border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900",
      className
    )}
    {...props}
  >
    <div className="flex h-14 items-center px-5">
      <div className={renderTitleStyle(titleWeight)}>{header}</div>
      <CollapsibleTrigger asChild>
        <Button ghost variant="light" isIconOnly>
          <FAIcon
            data-state={open ? "open" : "closed"}
            icon={faChevronDown}
            className={cn("border-0 transition duration-200 data-[state=open]:rotate-180")}
          />
          <span className="sr-only">Toggle</span>
        </Button>
      </CollapsibleTrigger>
    </div>
    <CollapsibleContent className="space-y-2 border-0 border-t border-solid border-gray-100 p-2 dark:border-gray-800">
      {children}
    </CollapsibleContent>
  </CollapsiblePrimitive.Root>
));
Collapsible.displayName = CollapsiblePrimitive.Root.displayName;

const CollapsibleRoot = CollapsiblePrimitive.Root;

const CollapsibleTrigger = CollapsiblePrimitive.CollapsibleTrigger;

const CollapsibleContent = CollapsiblePrimitive.CollapsibleContent;

export { Collapsible, CollapsibleRoot, CollapsibleTrigger, CollapsibleContent };
